import { Avatar, Box, Grid, GridProps, Stack, styled, Tooltip, Typography } from '@mui/material';
import { PopoverTwoColumns } from 'redesign/popover-two-column-table/popover-two-columns';
import { getFullNameWithInfix } from 'hq/components/views/flexWorkers/utils';
import { useMemo, useRef, useState } from 'react';
import { groupBy } from 'lodash';
import { DateTime } from 'luxon';
import { getColorForAvailabilityType } from './helpers/get-color-for-availability-type';
import { useGetPopoverInfoRows } from './hooks/use-get-popover-info-rows';
import { AvailabilityParsedItem } from '../availability/availability-calendar.types';

export const AvailabilityGridCell = ({
  availabilityBlocks,
  handleClick,
}: {
  availabilityBlocks: AvailabilityParsedItem[];
  //Likely to change as the actions for other segment types become more common
  handleClick: (...args: any[]) => void;
}) => {
  const segmentsByType = useMemo(() => {
    const sortedBlocks = [...availabilityBlocks].sort(
      (a, b) =>
        DateTime.fromISO(a.utcStartDateTime).toMillis() -
        DateTime.fromISO(b.utcStartDateTime).toMillis()
    );
    return groupBy(sortedBlocks, (segment) => segment.type);
  }, [availabilityBlocks]);

  const blockHasAvailabilitySegment = segmentsByType['available']?.length > 0;
  const availabilitySegments = segmentsByType['available'] ?? [];
  const blockHasTentativeSegment = segmentsByType['tentative']?.length > 0;
  const tentativeSegments = segmentsByType['tentative'] ?? [];
  const blockHasPendingSegment = segmentsByType['pending']?.length > 0;
  const pendingSegments = segmentsByType['pending'] ?? [];
  const blockHasBlockedSegment = segmentsByType['blocked']?.length > 0;
  const blockedSegments = segmentsByType['blocked'] ?? [];

  return (
    <StyledContainer direction="column" spacing={2} mt={1} mb={1}>
      {blockHasAvailabilitySegment ? (
        <StyledAvailabilityCell
          data={availabilitySegments}
          item
          data-testid={`availability-block-${availabilitySegments[0].workerForPopover?.workerId}`}
          onClick={() =>
            handleClick(
              availabilitySegments,
              getFullNameWithInfix(availabilitySegments[0].workerForPopover),
              availabilitySegments[0].workerForPopover?.workerId
            )
          }
        />
      ) : (
        <FillerBlock />
      )}
      {blockHasPendingSegment ? (
        <StyledPendingCell
          data={pendingSegments}
          item
          data-testid={`pending-block-${pendingSegments[0].workerForPopover?.workerId}`}
        />
      ) : (
        <FillerBlock />
      )}
      {blockHasTentativeSegment ? (
        <StyledTentativeCell
          data={tentativeSegments}
          item
          data-testid={`tentative-block-${tentativeSegments[0].workerForPopover?.workerId}`}
        />
      ) : (
        <FillerBlock />
      )}
      {blockHasBlockedSegment ? (
        <StyledBlockedCell
          data={blockedSegments}
          item
          data-testid={`blocked-block-${blockedSegments[0].workerForPopover?.workerId}`}
        />
      ) : (
        <FillerBlock />
      )}
    </StyledContainer>
  );
};

const AvailabilityCell = ({ data, ...props }: { data: AvailabilityParsedItem[] } & GridProps) => {
  const popoverInfoRows = useGetPopoverInfoRows(data);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const closeTimer = useRef<number | null>(null);

  const startHoverEffect = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setAnchorEl(e.currentTarget);
  };

  const stopHoverEffect = () => {
    closeTimer.current = window.setTimeout(() => {
      setAnchorEl(null);
    }, 100);
  };

  return (
    <>
      <Grid
        {...props}
        onMouseEnter={startHoverEffect}
        onMouseLeave={stopHoverEffect}
        sx={{ height: '100%', width: '100%' }}
      />
      <PopoverTwoColumns
        header={
          <>
            <WrapperBox>
              <StyledAvatarContainer>
                <Avatar src={data[0].workerForPopover?.profilePictureUrl} alt="Profile Picture" />
              </StyledAvatarContainer>
              {data[0].workerForPopover?.partnerName ? (
                <Tooltip title={data[0].workerForPopover.partnerName}>
                  <LabelBox>{data[0].workerForPopover.partnerName}</LabelBox>
                </Tooltip>
              ) : null}
            </WrapperBox>
            <NameBox>
              <Typography variant="body1" fontWeight={600} noWrap>
                {getFullNameWithInfix(data[0].workerForPopover)}
              </Typography>
            </NameBox>
          </>
        }
        data={popoverInfoRows}
        maxWidth={350}
        maxHeight={400}
        anchorEl={anchorEl}
      />
    </>
  );
};

const StyledAvailabilityCell = styled(AvailabilityCell)(({ theme }) => ({
  backgroundColor: getColorForAvailabilityType('available', 0.2),
  ':hover': {
    backgroundColor: getColorForAvailabilityType('available', 1),
    border: '1px solid',
    borderColor: theme.palette.lwPrimaryAccentLight?.[5],
    boxShadow: `0 12px 32px ${getColorForAvailabilityType('available', 0.5)}`,
  },
  minHeight: '15px',
}));

const StyledBlockedCell = styled(AvailabilityCell)(({ theme }) => ({
  backgroundColor: getColorForAvailabilityType('blocked', 0.1),
  ':hover': {
    backgroundColor: getColorForAvailabilityType('blocked', 0.4),
    border: '1px solid',
    borderColor: theme.palette.lwPrimaryAccentLight?.[5],
    boxShadow: `0 12px 32px ${getColorForAvailabilityType('blocked', 0.5)}`,
  },
  minHeight: '15px',
}));

const StyledTentativeCell = styled(AvailabilityCell)(({ theme }) => ({
  backgroundColor: getColorForAvailabilityType('tentative', 0.4),
  ':hover': {
    backgroundColor: getColorForAvailabilityType('tentative', 1),
    border: '1px solid',
    borderColor: theme.palette.lwPrimaryAccentLight?.[5],
    boxShadow: `0 12px 32px ${getColorForAvailabilityType('tentative', 0.5)}`,
  },
  minHeight: '15px',
}));

const StyledPendingCell = styled(AvailabilityCell)(({ theme }) => ({
  backgroundColor: getColorForAvailabilityType('pending', 0.4),
  ':hover': {
    backgroundColor: getColorForAvailabilityType('pending', 1),
    border: '1px solid',
    borderColor: theme.palette.lwPrimaryAccentLight?.[5],
    boxShadow: `0 12px 32px ${getColorForAvailabilityType('pending', 0.5)}`,
  },
  minHeight: '15px',
}));

//Keeps the segments aligned while maintaining responsiveness
const FillerBlock = styled(Box)({
  height: '15px',
});

const StyledContainer = styled(Stack)({
  width: '100%',
  boxSizing: 'border-box',
  flexWrap: 'nowrap',
});

const StyledAvatarContainer = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
}));

const LabelBox = styled(Box)(() => ({
  border: '1px solid #eeeeee',
  borderRadius: '16px',
  backgroundColor: 'white',
  padding: '0 8px',
  marginTop: '-8px',
  lineHeight: '12px',
  fontSize: '8px',
  textAlign: 'center',
  zIndex: 100,
  textOverflow: 'ellipsis',
  overflow: 'hidden',
}));

const WrapperBox = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '40%',
}));

const NameBox = styled(Box)(() => ({
  maxWidth: 300,
  minWidth: 0,
}));
